import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// The ten limits of the count-based free trial, in the order the PUT body lists them
export const TRIAL_LIMIT_KEYS = [
  'total_limit',
  'max_posters',
  'max_postcards',
  'max_pdfs_sarpanch',
  'max_pdfs_wardpanch',
  'max_sub_ids',
  'tag_limit',
  'lock_block',
  'max_searches',
  'max_slip_shares',
] as const;

export type TrialLimitKey = typeof TRIAL_LIMIT_KEYS[number];

export type TrialLimits = Record<TrialLimitKey, number>;

// Built-in values while the owner has never saved the setting
export const DEFAULT_TRIAL_LIMITS: TrialLimits = {
  total_limit: 50,
  max_posters: 10,
  max_postcards: 5,
  max_pdfs_sarpanch: 3,
  max_pdfs_wardpanch: 1,
  max_sub_ids: 2,
  tag_limit: 30,
  lock_block: 5,
  max_searches: 30,
  max_slip_shares: 5,
};

export interface TrialLimitField {
  key: TrialLimitKey;
  label: string;
  min: number;
  max: number;
  help: string;
}

// Allowed ranges are the backend's (anything outside is refused with 422)
export const TRIAL_LIMIT_FIELDS: TrialLimitField[] = [
  {
    key: 'total_limit',
    label: 'Total trial actions',
    min: 1,
    max: 100000,
    help: 'PDF uploads + colour tags + AI posters + AI postcards + searches + slip shares together. Reached = trial over.',
  },
  { key: 'max_posters', label: 'AI posters', min: 0, max: 1000, help: 'Template posters and edits count too.' },
  { key: 'max_postcards', label: 'AI postcards', min: 0, max: 1000, help: 'Separate from the posters.' },
  {
    key: 'max_pdfs_sarpanch',
    label: 'PDF uploads (Sarpanch / PS / ZP)',
    min: 0,
    max: 100,
    help: 'Sarpanch, Panchayat Samiti and Zila Parishad.',
  },
  {
    key: 'max_pdfs_wardpanch',
    label: 'PDF uploads (Wardpanch)',
    min: 0,
    max: 100,
    help: 'Wardpanch, and an Admin with no post.',
  },
  { key: 'max_sub_ids', label: 'Sub-IDs', min: 0, max: 100, help: 'Outside the total.' },
  { key: 'tag_limit', label: 'Colour tags', min: 0, max: 100000, help: 'Every applied tag counts, a re-tag too.' },
  {
    key: 'lock_block',
    label: 'Voter lock run length',
    min: 1,
    max: 100,
    help: 'Voter list visible and locked in runs of this many serial numbers.',
  },
  {
    key: 'max_searches',
    label: 'Voter searches',
    min: 0,
    max: 100000,
    help: 'Voters opened from Search.',
  },
  {
    key: 'max_slip_shares',
    label: 'Voter slip shares (single voter)',
    min: 0,
    max: 100000,
    help: "How many times a trial campaign may share one voter's slip (Share or WhatsApp). Each share also counts towards the total limit. 0 = no slip sharing in the trial.",
  },
];

export interface TrialSettingsHistoryItem extends TrialLimits {
  id?: string;
  created_at: string;
  created_by: string | null;
}

export interface TrialSettings extends TrialLimits {
  // True while the owner has never saved the setting (the values are the built-in defaults)
  is_default: boolean;
  updated_at: string | null;
  updated_by: string | null;
  // Every change, newest first (at most 100)
  history: TrialSettingsHistoryItem[];
}

export interface TrialSettingsState {
  settings: TrialSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: TrialSettingsState = {
  settings: null,
  loading: false,
  saving: false,
  error: null,
};

const trialSettingsSlice = createSlice({
  name: 'trialSettings',
  initialState,
  reducers: {
    setTrialSettingsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTrialSettingsSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    setTrialSettingsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setTrialSettings: (state, action: PayloadAction<TrialSettings>) => {
      state.settings = { ...action.payload, history: action.payload.history || [] };
    },
  },
});

export const { setTrialSettingsLoading, setTrialSettingsSaving, setTrialSettingsError, setTrialSettings } =
  trialSettingsSlice.actions;

export default trialSettingsSlice.reducer;
